import { FC, useState } from "react";
import { Box, Select, Text } from "@postenbring/hedwig-react";
import { OAuthClient } from "../infrastructure/api/api";
import Translated from "../infrastructure/translation/Translated";
import { useTranslate } from "../infrastructure/translation/useTranslate";
import { TranslationKey } from "../infrastructure/translation/translator";
import {
  useApplications,
  useMidpUrl,
} from "../infrastructure/entrypoint/EntrypointContext";
import { DetailList, DetailRow } from "../DetailList/DetailList";
import { CopyButton } from "./CopyButton";
import { useAvailableAccounts } from "./clientsRouteContext";
import { scopeOptions } from "./scopeUtils";
import styles from "./ClientOAuthDetails.module.css";

const GRANT_TYPE = "client_credentials";

const Value: FC<{ value: string; labelKey: TranslationKey }> = ({
  value,
  labelKey,
}) => (
  <span className={styles.value}>
    <code className={styles.code}>{value}</code>
    <CopyButton value={value} labelKey={labelKey} />
  </span>
);

export const ClientOAuthDetails: FC<{ client: OAuthClient }> = ({ client }) => {
  const translate = useTranslate();
  const applications = useApplications();
  const availableAccounts = useAvailableAccounts();
  const midpUrl = useMidpUrl();

  const options = scopeOptions(client.scopes, applications, availableAccounts);
  const [selectedScope, setSelectedScope] = useState(options[0]?.scope);

  const selected =
    options.find((option) => option.scope === selectedScope) ?? options[0];

  return (
    <Box variant="white" className={styles.card}>
      <div className={styles.heading}>
        <Text variant="h2" as="h2" size="min">
          <Translated textKey={"client.oauth_details_header"} />
        </Text>
        <p className={styles.description}>
          <Translated textKey={"client.oauth_details_description"} />
        </p>
        <p className={styles.description}>
          <Translated textKey={"client.single_scope_per_token"} />
        </p>
      </div>

      {selected ? (
        <>
          {options.length > 1 && (
            <Select
              label={translate("client.select_scope_label")}
              value={selected.scope}
              onChange={(e) => setSelectedScope(e.target.value)}
            >
              {options.map((option) => (
                <option key={option.scope} value={option.scope}>
                  {option.label}
                </option>
              ))}
            </Select>
          )}

          <DetailList>
            {midpUrl && (
              <DetailRow
                label={<Translated textKey={"client.token_endpoint_label"} />}
              >
                <Value value={midpUrl} labelKey="client.copy_token_endpoint" />
                <p className={styles.explanation}>
                  <Translated textKey={"client.token_endpoint_explanation"} />
                </p>
              </DetailRow>
            )}

            <DetailRow label={<Translated textKey={"client.client_id"} />}>
              <Value value={client.clientId} labelKey="client.copy_client_id" />
            </DetailRow>

            <DetailRow
              label={<Translated textKey={"client.grant_type_label"} />}
            >
              <Value value={GRANT_TYPE} labelKey="client.copy_grant_type" />
              <p className={styles.explanation}>
                <Translated textKey={"client.grant_type_explanation"} />
              </p>
            </DetailRow>

            <DetailRow label={<Translated textKey={"client.scope_label"} />}>
              <Value value={selected.scope} labelKey="client.copy_scope" />
              <p className={styles.explanation}>
                <Translated textKey={"client.scope_format_explanation"} />{" "}
                <code className={styles.code}>
                  <Translated textKey={"client.scope_format_example"} />
                </code>
              </p>
              <p className={styles.explanation}>
                <Translated textKey={"client.scope_client_library_note"} />
              </p>
            </DetailRow>

            <DetailRow label={<Translated textKey={"client.resource_label"} />}>
              {selected.resource ? (
                <Value
                  value={selected.resource}
                  labelKey="client.copy_resource"
                />
              ) : (
                <span className={styles.empty}>
                  <Translated textKey={"client.resource_unknown"} />
                </span>
              )}
              <p className={styles.explanation}>
                <Translated textKey={"client.resource_explanation"} />
              </p>
            </DetailRow>
          </DetailList>
        </>
      ) : (
        <p className={styles.empty}>
          <Translated textKey={"client.no_scopes_no_token"} />
        </p>
      )}
    </Box>
  );
};
